import { CallLog, CallParticipant, CallRecording } from 'services/callLogs/types'
import { LegacyCallSession } from 'services/callLogsLegacy/types'
import actions from './actions'

/** Platform is derived from `device_id`, which is an empty string on many real rows. */
export type LegacyParticipantPlatform =
  | 'Web'
  | 'Android'
  | 'IOS'
  | 'Ionic'
  | 'React Native'
  | 'Other'

/** A v1 participant after the users-API join; `name`/`role`/`avatar` are not in the v1 payload. */
/** Epoch MILLISECONDS — the window the last legacy search actually used. */
export interface LegacyCallLogsRange {
  fromDate: number
  toDate: number
}

export interface LegacyCallParticipantRow {
  uid: string
  name: string
  role: string
  isDeleted: boolean
  platform: LegacyParticipantPlatform
  audioMinutes: number | '-'
  videoMinutes: number | '-'
  avatar?: string
  start_time?: number
}

/** Every field is optional: the handlers reset this to `{}` on empty or error. */
export interface LoosePagination {
  current_page?: number
  total?: number
  per_page?: number
  count?: number
  total_pages?: number
}

export interface CallsState {
  callSearchLogs: CallLog[]
  callSearchLogsTotal: number
  callSearchLogsPagination: LoosePagination
  areCallLogsLoading: boolean

  /** Null when no call is loaded, never `[]`, so consumers guard one shape. */
  callActive: CallLog | null
  callSessionParticipants: CallParticipant[]
  callRecordings: CallRecording[]
  areCallDetailsLoading: boolean

  // v1 metrics API, behind /calls-legacy. Kept separate: the record shapes differ.
  legacyCallSearchLogs: LegacyCallSession[]
  legacyCallLogsRange: LegacyCallLogsRange | null
  areLegacyCallLogsLoading: boolean
  /** Null when no call is loaded, never `[]`, so consumers guard one shape. */
  legacyCallActive: LegacyCallSession | null
  legacyCallSessionParticipants: LegacyCallParticipantRow[]
  areLegacyCallDetailsLoading: boolean
  areLegacyCallParticipantsLoading: boolean
}

const initialState: CallsState = {
  callSearchLogs: [],
  callSearchLogsTotal: 0,
  callSearchLogsPagination: {},
  areCallLogsLoading: false,

  callActive: null,
  callSessionParticipants: [],
  callRecordings: [],
  areCallDetailsLoading: false,

  legacyCallSearchLogs: [],
  legacyCallLogsRange: null,
  areLegacyCallLogsLoading: false,
  legacyCallActive: null,
  legacyCallSessionParticipants: [],
  areLegacyCallDetailsLoading: false,
  areLegacyCallParticipantsLoading: false,
}

interface SetStateAction {
  type: string
  payload?: Partial<CallsState>
}

export default function callsReducer(
  state: CallsState = initialState,
  action: SetStateAction,
): CallsState {
  switch (action.type) {
    case actions.SET_STATE:
      return { ...state, ...action.payload }
    default:
      return state
  }
}
